import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {AuthService} from "../../services/auth.service";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {Subscription, switchMap, catchError, of} from "rxjs";
import {PublicWorkService} from "src/app/modules/works/services/public-work.service";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {HeaderComponent} from "../../components/header/header.component";
import {AccountActivationFormComponent} from "../../components/account-activation-form/account-activation-form.component";
import {takeRecoveryToken} from "../../utils/recovery-token.util";

@Component({
  selector: "app-account-activation",
  templateUrl: "./account-activation.component.html",
  styleUrls: ["./account-activation.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeaderComponent,
    RouterLink,
    AccountActivationFormComponent,
    TranslateModule,
  ],
})
export class AccountActivationComponent implements OnInit, OnDestroy {
  error?: CustomError;
  loading = true;
  activationToken!: string;
  isTokenValid = false;
  workResumeUrl?: string;
  private readonly userLanguage = navigator.language.startsWith("fr")
    ? "fr"
    : "en";
  private readonly subscriptions: Subscription[] = [];
  private onboardingRedirectTimer?: ReturnType<typeof setTimeout>;
  private activationSubscription?: Subscription;

  constructor(
    private readonly translate: TranslateService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly publicWorks: PublicWorkService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.translate.defaultLang = this.userLanguage;
    this.translate.reloadLang(this.userLanguage);
    this.translate.use(this.userLanguage);

    const routeSubscription = this.route.params.subscribe((params) => {
      this.activationSubscription?.unsubscribe();
      clearTimeout(this.onboardingRedirectTimer);
      this.error = undefined;
      this.loading = true;
      this.isTokenValid = false;
      this.activationToken = takeRecoveryToken(
        "account-activation",
        params["token"]
      );

      this.activationSubscription = this.authService
        .activateAccount(this.activationToken)
        .pipe(
          switchMap(() =>
            this.publicWorks.resume().pipe(catchError(() => of({url: null})))
          )
        )
        .subscribe({
          next: (resume) => {
            this.workResumeUrl = resume.url ?? undefined;
            this.loading = false;
            this.isTokenValid = true;
            this.onboardingRedirectTimer = setTimeout(() => {
              void this.router.navigate(["/onboarding"], {
                queryParams: {redirectTo: this.workResumeUrl},
              });
            }, 5000);
            this.cdr.markForCheck();
          },
          error: (error: CustomError) => {
            this.loading = false;
            this.error = error;
            this.cdr.markForCheck();
          },
        });
    });
    this.subscriptions.push(routeSubscription);
  }

  ngOnDestroy() {
    this.activationSubscription?.unsubscribe();
    clearTimeout(this.onboardingRedirectTimer);
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
